import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Button from "./Button";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import relativeTime from "dayjs/plugin/relativeTime";
import { tokens } from "../../theme";
import { useAuthSlice } from "../../redux/slice/authSlice";
import { AttachFile, CancelOutlined, KeyboardVoice, Send } from "@mui/icons-material";
import CustomTextField from "./CustomTextField";
import SpeechToTextPopup from "./SpeechToTextPopup";
import { convertBase64 } from "../../utils/Utility";

dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(relativeTime);

function getFriendlyDateLabel(dateStr: string) {
  const date = dayjs(dateStr);

  if (date.isToday()) return "Today";
  if (date.isYesterday()) return "Yesterday";

  const now = dayjs();
  const diffInDays = now.diff(date, "day");

  if (diffInDays >= 2 && diffInDays <= 7) return "Last week";

  return date.format("MMMM YYYY");
}

type TimelineViewProps = {
  control : any;
  setValue: any;
  getValues: any;
  title: string;
  name?: string;
  maxHeight?: number | string;
  timelineData: {
    date: string;
    entries: {
      text: string;
      time: string;
      attachment?: string;
    };
  }[];
};

export default function TimelineView({
  control,
  setValue,
  getValues,
  title,
  name,
  maxHeight = 400,
  timelineData,
}: TimelineViewProps) {

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { userInfo } = useAuthSlice();
  const [comment, setComment] = useState("");
  const [commentsList, setCommentsList] = useState(timelineData);
  const [isVoicePopupOpen, setIsVoicePopupOpen] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  useEffect(() => {
    setCommentsList(timelineData);
  }, [timelineData]);

  const removeDataUriPrefix = (dataUri: string): string => {
    const base64String = dataUri.split(",")[1];
    return base64String || "";
  };

  const handleRemoveFile = () => {
    setSelectedFileName(null);
    setValue("attachment", "");
  };

  const handleNotDocumentFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      try {
        const files = event.target.files;
        if (files && files.length > 0) {
          const file = files[0];
          const base64File = await convertBase64(file);
  
          setValue("attachment", removeDataUriPrefix(String(base64File)));
          setValue("documentName", file.name);
          setValue("fileType", file.type);
          setValue("noteType", "INVOICE ADDITIONAL NOTE");
          setValue("xrefTable", "INVOICE");
          setSelectedFileName(file.name);
        } else {
          console.error("No files selected or invalid input.");
        }
      } catch (error) {
        console.error("Error processing the file:", error);
      }
    };

  const handleSubmitComment = () => {
    const currentComment = getValues("comment")?.trim();
    const attachment = getValues("attachment");
    const documentName = getValues("documentName");
    const fileType = getValues("fileType");

    if (currentComment || attachment) {
      const newEntry = {
        date: dayjs().format("YYYY-MM-DD"),
        entries: {
          text: `${currentComment || ""}`,
          time: dayjs().format("hh:mm A"),
          attachment: attachment ? ` (Attached: ${documentName})` : "",
        },
      };

      setCommentsList((prev) => [newEntry, ...prev]);

      // Clear form values
      setValue("comment", "");
      setValue("attachment", "");
      setValue("documentName", "");
      setValue("fileType", "");
      setValue("noteType", "");
      setValue("xrefTable", "");

      // Clear file state
      setSelectedFileName(null);
    }
  };


  return (
    <Box
      className="custom-scroll"
      sx={{ px: 2, py: 2, mx: "auto", maxHeight, overflowY: "auto" }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        sx={{
          color: colors.success[300],
          mb:2,
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: "5px",
          width: "100%",
        }}
      >
        <Tooltip title="Speech to text" placement="top" arrow>
          <IconButton
            color="default"
            component="label"
            sx={{
              p: 2,
              mr: 1,
              borderRadius: 2,
              bgcolor: "#1976d2",
              transition: "background-color 0.4s ease",
              "&:hover": {
                bgcolor:  "#1565c0",
              },
            }}
            onClick={() => setIsVoicePopupOpen(true)}
          >
            <KeyboardVoice />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Attach File" placement="top" arrow>
          <IconButton
           color="default"
           component="label"
           sx={{
             p: 2,
             mr: 1,
             borderRadius: 2,
             backgroundColor: colors.grey[700],
           }}
         >
           <AttachFile />
           <input
             type="file"
             hidden
             onChange={handleNotDocumentFileChange}
           />
         </IconButton>
        </Tooltip>
        <CustomTextField
          controllerName="comment"
          control={control}
          label="Leave a comment"
          multiline
          rules={{ required: "Notes are required." }}
          // value={comment}
          // onChange={(e) => setComment(e.target.value)}
        />
        <IconButton
          sx={{
            p: 2,
            ml: 1,
            borderRadius: 2,
            backgroundColor: colors.warning[300],
          }}
          onClick={handleSubmitComment}
        >
          <Send sx={{ color: colors.grey[900] }} />
        </IconButton>
      </Box>
      <Box>
        {selectedFileName && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              color: colors.info[500],
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontStyle: "italic",
                mr: 1,
                color: colors.grey[100],
              }}
            >
              {selectedFileName}
            </Typography>
            <IconButton
              size="small"
              onClick={handleRemoveFile}
              sx={{
                color: colors.error[300],
              }}
            >
              <CancelOutlined />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          bgcolor: colors.grey[900],
          borderColor: colors.grey[700],
          borderRadius: 2,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          gap={2}
          alignItems="center"
        >
          <Avatar sx={{ bgcolor: "#f81b60", fontWeight: "bold" }}>
            {(name || userInfo.userName)?.charAt(0).toUpperCase()}
          </Avatar>
          <Box flex={1}>
            <TextField
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              variant="standard"
              fullWidth
              placeholder="Leave a comment..."
              InputProps={{ disableUnderline: true }}
              sx={{ mb: 1 }}
            />
          </Box>

          <Button
            size="small"
            variant="outlined"
            color="warning"
            sx={{ textTransform: "none" }}
            onClick={() => {
              if (comment.trim()) {
                const newEntry = {
                  date: dayjs().format("YYYY-MM-DD"),
                  entries: {
                    text: comment,
                    time: dayjs().format("hh:mm A"),
                  },
                };
                setCommentsList((prev) => [newEntry, ...prev]);
                setComment("");
              }
            }}
          >
            Post
          </Button>
        </Box>
      </Paper> */}

      <Box
        sx={{
          position: "relative",
          pl: 2,
          pr: 2,
          mt:2,
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: 2,
            bgcolor: colors.grey[700],
            transform: "translateX(-50%)",
          }}
        />

        {commentsList.map((item, i) => (
          <Box key={i} sx={{ mb: 4, position: "relative" }}>
            <Box
              sx={{
                position: "relative",
                mb: 4,
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  transform: "translateX(-50%)",
                  width: 14,
                  height: 14,
                  borderRadius: "100%",
                  bgcolor: colors.grey[900],
                  border: `2px solid ${colors.grey[600]}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "100%",
                    bgcolor: colors.grey[500],
                  }}
                />
              </Box>
              
              <Box sx={{ ml: 3, width: "100%" }}>
                <Stack direction="row" justifyContent="space-between">
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {item.entries.text}
                    </Typography>
                    {item.entries.attachment && (
                      <Box>
                        <Typography variant="body2" 
                        sx={{
                          fontStyle: "italic",
                          mr: 1,
                          color: colors.grey[400],
                        }}>
                          {item.entries.attachment}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box textAlign="end">
                    <Typography
                      variant="subtitle2"
                      sx={{ color: colors.grey[600] }}
                    >
                      {getFriendlyDateLabel(item.date)}
                    </Typography>
                    <Typography variant="caption" color="gray">
                      {item.entries.time}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      {isVoicePopupOpen && (
        <SpeechToTextPopup
          open={isVoicePopupOpen}
          onClose={() => {setIsVoicePopupOpen(false)}}
          onSubmitVoice={(data: any)=>setValue("comment",data)}
        />
      )}
    </Box>
  );
}
